<script setup lang="ts">
import { map, mapValues } from "lodash-es";
import { NavArrowRight } from "@iconoir/vue";
import moment from "moment-timezone";
import { useFormat } from "~/composables/use-format";

defineOptions({ name: "ProductUsageReport" });

const { t } = useI18n();
const { n } = useFormat();
const { $api } = useNuxtApp();
const toast = useToast();
const { set: setTitle } = usePageTitle();

interface UsageSeriesRecord {
  id: string;
  label: string;
  start?: number;
  activeWorkspaces: number;
  activeUsers: number;
  orders: number;
  events: number;
  sites: number;
  products: number;
  emails: number;
  aiSessions: number;
  total: number;
}
interface UsageAnalytics {
  overview: {
    id: string;
    activeWorkspaces: number;
    activeUsers: number;
    inactiveWorkspaces: number;
    totalWorkspaces: number;
  };
  series: UsageSeriesRecord[];
}

const timezone = "Asia/Ho_Chi_Minh";
// Matches the picker's "Last 30 days" preset exactly, so the field shows
// that label and leaves its row active.
const filter = reactive({
  from: moment.tz(timezone).subtract(29, "days").startOf("day").unix(),
  to: moment.tz(timezone).endOf("day").unix(),
  groupBy: "week",
});
const query = computed(() => ({
  ...(filter.from ? { from: filter.from } : {}),
  ...(filter.to ? { to: filter.to } : {}),
  groupBy: filter.groupBy,
}));
const groupByItems = computed(() => [
  { value: "week", label: t("analytics.groupBy.week") },
  { value: "month", label: t("analytics.groupBy.month") },
]);

const firstLoading = ref(true);
// firstLoading only ever falls once; this one tracks every refetch too, so a
// filter change shows the charts working instead of sitting on stale numbers.
const loading = ref(false);
const data = ref<UsageAnalytics | null>(null);
const byWorkspace = ref<UsageSeriesRecord[]>([]);

const labels = computed(() => map(data.value?.series ?? [], "label"));

const workspaceRows = computed(() =>
  map(byWorkspace.value, (workspace) =>
    mapValues(workspace, (value, key) =>
      key === "id" || key === "label" ? value : n(value as number, "integer"),
    ),
  ),
);

const activityChartOption = computed(() => ({
  tooltip: { trigger: "axis" },
  legend: { data: [t("analytics.kpi.activeWorkspaces"), t("analytics.kpi.activeUsers")] },
  xAxis: { type: "category", data: labels.value },
  yAxis: { type: "value", minInterval: 1 },
  series: [
    {
      name: t("analytics.kpi.activeWorkspaces"),
      type: "line",
      areaStyle: { opacity: 0.08 },
      data: map(data.value?.series ?? [], "activeWorkspaces"),
    },
    {
      name: t("analytics.kpi.activeUsers"),
      type: "line",
      data: map(data.value?.series ?? [], "activeUsers"),
    },
  ],
}));

const actionKeys = [
  { key: "orders", field: "orders" },
  { key: "events", field: "events" },
  { key: "sites", field: "sites" },
  { key: "products", field: "products" },
  { key: "emails", field: "emails" },
  { key: "ai", field: "aiSessions" },
] as const;

const actionsChartOption = computed(() => ({
  tooltip: { trigger: "axis" },
  legend: { data: actionKeys.map((a) => t(`analytics.action_${a.key}`)) },
  xAxis: { type: "category", data: labels.value },
  yAxis: { type: "value", minInterval: 1 },
  series: actionKeys.map((a) => ({
    name: t(`analytics.action_${a.key}`),
    type: "bar",
    stack: "actions",
    data: map(data.value?.series ?? [], a.field),
  })),
}));

async function load() {
  loading.value = true;
  try {
    const [trend, workspaces] = await Promise.all([
      $api<UsageAnalytics>("/analytics/usage", { query: query.value }),
      $api<UsageAnalytics>("/analytics/usage", { query: { ...query.value, groupBy: "workspace" } }),
    ]);
    data.value = trend;
    byWorkspace.value = workspaces.series;
    firstLoading.value = false;
  } catch {
    toast.error({ title: t("message.loadFailed") });
  } finally {
    loading.value = false;
  }
}

watch(query, () => {
  load();
});

onMounted(async () => {
  setTitle(t("analytics.usageTitle"));
  await load();
});
</script>

<template>
  <div class="flex flex-col gap-large">
    <div class="flex flex-col gap-xsmall md:flex-row md:items-center md:justify-between">
      <SegmentedControl v-model="filter.groupBy" :items="groupByItems" size="small" />
      <FormDateRange
        v-model:from="filter.from"
        v-model:to="filter.to"
        :timezone="timezone"
        size="sm"
      />
    </div>

    <div class="grid grid-cols-2 gap-medium md:grid-cols-4">
      <MetricCard
        :loading="firstLoading"
        :label="t('analytics.kpi.activeWorkspaces')"
        :hint="t('analytics.usageHelp')"
        :value="n(data?.overview.activeWorkspaces ?? 0, 'integer')"
      />
      <MetricCard
        :loading="firstLoading"
        :label="t('analytics.kpi.activeUsers')"
        :value="n(data?.overview.activeUsers ?? 0, 'integer')"
      />
      <MetricCard
        :loading="firstLoading"
        :label="t('analytics.kpi.inactiveWorkspaces')"
        :value="n(data?.overview.inactiveWorkspaces ?? 0, 'integer')"
        :intent="(data?.overview.inactiveWorkspaces ?? 0) > 0 ? 'negative' : 'neutral'"
      />
      <MetricCard
        :loading="firstLoading"
        :label="t('analytics.kpi.totalWorkspaces')"
        :value="n(data?.overview.totalWorkspaces ?? 0, 'integer')"
      />
    </div>

    <div class="grid grid-cols-1 gap-medium lg:grid-cols-2">
      <SectionCard :title="t('analytics.chart.activity')">
        <Chart :option="activityChartOption" :loading="loading" :height="300" />
      </SectionCard>
      <SectionCard :title="t('analytics.chart.actions')">
        <Chart :option="actionsChartOption" :loading="loading" :height="300" />
      </SectionCard>
    </div>

    <Card v-if="firstLoading || workspaceRows.length" class="flex flex-col gap-small">
      <DataTable
        :columns="[
          { key: 'label', label: t('analytics.field.workspace'), class: 'md:min-w-[240px]' },
          { key: 'orders', label: t('analytics.action_orders'), align: 'right', class: 'min-w-[96px]' },
          { key: 'events', label: t('analytics.action_events'), align: 'right', class: 'min-w-[96px]' },
          { key: 'sites', label: t('analytics.action_sites'), align: 'right', class: 'min-w-[96px] hidden md:table-cell' },
          { key: 'products', label: t('analytics.action_products'), align: 'right', class: 'min-w-[96px] hidden md:table-cell' },
          { key: 'emails', label: t('analytics.action_emails'), align: 'right', class: 'min-w-[112px] hidden md:table-cell' },
          { key: 'aiSessions', label: t('analytics.action_ai'), align: 'right', class: 'min-w-[112px] hidden md:table-cell' },
          { key: 'total', label: t('analytics.field.totalActions'), align: 'right', class: 'min-w-[112px]' },
          { key: 'actions', label: '', align: 'right', class: 'min-w-[64px]' },
        ]"
        :items="workspaceRows"
        row-key="id"
      >
        <template #cell-label="{ row }">
          <TableLink :to="`/workspace/${row.id}`">{{ row.label || "—" }}</TableLink>
        </template>
        <template #cell-total="{ row }">
          <span class="content-neutral-strong">{{ row.total }}</span>
        </template>
        <template #actions="{ row }">
          <NuxtLink :to="`/workspace/${row.id}`" class="inline-flex" :aria-label="row.label">
            <ButtonIcon variant="flat-neutral" size="small">
              <NavArrowRight class="icon" />
            </ButtonIcon>
          </NuxtLink>
        </template>
      </DataTable>
      <!-- the list is capped, so saying so beats looking complete -->
      <span class="text-body-small-tertiary content-neutral-medium">{{ t("analytics.byWorkspaceHelp") }}</span>
    </Card>
  </div>
</template>
